import { INotificationContactPoint } from "@/Common/Api/Contacts/Dto/INotificationContactPoint";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { ValidationRule } from "@/Common/Api/Validation/Dto/Responses/ValidationRule";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";

class PhoneNumber implements INotificationContactPoint {
    public ContactPoint_id: string = GuidHelper.Empty();
    public Number: string = "";
    public CountryLookupId: string = "";
    public StateLookupId: string = "";
    public Notes: string = "";
    public IsPreferred: boolean = false;
    public Extension?: string;
    public PhoneTypeLookupId: string = "";
    public ContactPointTypeLookupId: string = ""; // only when Loading from PhoneNumberLight

    // for edit:
    public IsValid: boolean = false;
    public AvailableCountryLookupIds: FlatLookupView[] = [];
    public AvailablePhoneTypeLookupIds: [FlatLookupView, FlatLookupView][] = [];
    public SortingValue: string = "";
    public isSelectedForNotification: boolean = false;
    public NotificationsEnabled: boolean = false;

    /**
     * The reference ID used for client-side rendering
     */
    public renderId: string;

    constructor(init?: Partial<PhoneNumber>) {
        this.renderId = sessionStore.newId().toString();
        this.updateModel(init);
    }

    public updateModel(init?: Partial<PhoneNumber>): PhoneNumber {
        if (init) {
            Object.assign(this, init);

            this.CountryLookupId = init.CountryLookupId ?? cacheStore.GetCountryLookups()[0].LookupID;
            this.PhoneTypeLookupId = init.PhoneTypeLookupId ?? cacheStore.GetPhoneContactPointTypeLookupTuples()[0][0].LookupID;
            this.ContactPointTypeLookupId = init.ContactPointTypeLookupId ?? cacheStore.GetPhoneContactPointTypeLookupTuples()[0][0].LookupID;
        }
        return this;
    }

    public static newPhone(contactPointType: ContactPointType, phoneType: PhoneType): PhoneNumber {
        return new PhoneNumber({
            AvailableCountryLookupIds: cacheStore.GetCountryLookups(),
            CountryLookupId: cacheStore.GetDefaultCountryForActiveSite().LookupID,
            AvailablePhoneTypeLookupIds: cacheStore.GetPhoneContactPointTypeLookupTuples(),
            PhoneTypeLookupId: cacheStore.getLookupByPhoneType(phoneType).LookupID,
            ContactPointTypeLookupId: cacheStore.getLookupByContactPointType(contactPointType).LookupID,
            renderId: sessionStore.newId().toString(),
            ContactPoint_id: GuidHelper.Empty()
        });
    }

    public static IsEmpty(phone: PhoneNumber): boolean {
        return !phone.Number?.length && !phone.Notes?.length;
    }

    public static GetFormattedPhoneNumber(position: number, phones: PhoneNumber[], format: string = "+{0} ({1}) {2} {3} {4}", formatExtension: string = " Ext.{Ext}"): string {
        if (phones?.length > position && phones[position]?.Number?.length >= 10) {
            const phonetype = phones[position].PhoneTypeLookupId
                ? `(${cacheStore.GetLocalizedValueByLookupId(phones[position].ContactPointTypeLookupId)} - ${cacheStore.GetLocalizedValueByLookupId(phones[position].PhoneTypeLookupId)})`
                : "";

            const countryLK = cacheStore.GetLookupById(phones[position].CountryLookupId);

            let output = format;
            if (countryLK.LookupCode == "CNMEXICO") {
                output = output.replace("{0}", "52");
                output = output.replace("{1}", phones[position].Number.substr(0, 2));
                output = output.replace("{2}", phones[position].Number.substr(2, 4));
                output = output.replace("{3}", phones[position].Number.substr(6, 4));
            }
            else {
                output = output.replace("{0}", countryLK.LookupCode === "CNCOLOMBIA" ? "57" : "1");
                output = output.replace("{1}", phones[position].Number.substr(0, 3));
                output = output.replace("{2}", phones[position].Number.substr(3, 3));
                output = output.replace("{3}", phones[position].Number.substr(6, 4));
            }

            output = output.replace("{4}", phonetype);

            if (!IsNullOrWhiteSpace(formatExtension)) {
                output += formatExtension.replace("{Ext}", phones[position].Extension ?? "");
            }

            return output;
        }
        else
            return "";
    }

    public static stringFormattedPhone(phone: PhoneNumber, includeExtension: boolean = false): string {
        let countryLookupId = phone.CountryLookupId;
        let phoneNumber = phone.Number;

        if (IsNullOrWhiteSpace(countryLookupId))
            countryLookupId = cacheStore.GetCountryLookups()[0].LookupID;

        let validationRule: ValidationRule;
        let displayFormatSelector: string = "";
        let displayFormatSubstitution: string = "";
        validationRule = cacheStore.validationHelper.getPhoneNumberValidationRule(countryLookupId, null);
        displayFormatSelector = validationRule ? validationRule.DisplayFormatSelectorPatern : "";
        displayFormatSubstitution = (validationRule) ? validationRule.DisplayFormatSubstitution : "";

        let formattedPhoneNumber = cacheStore.validationHelper.getFormatedString(phoneNumber, displayFormatSelector, displayFormatSubstitution);
        if (includeExtension && phone.Extension) {
            if (!IsNullOrWhiteSpace(phone.Extension))
                formattedPhoneNumber += " Ext. " + phone.Extension;
        }

        return formattedPhoneNumber;
    }

    public static hasExtensionField(phoneLookupId: string): boolean {
        return phoneLookupId == cacheStore.getLookupByPhoneType(PhoneType.Phone).LookupID;
    }

    public static getValidationErrorMessage(phone: PhoneNumber): string {
        let validationRule = cacheStore.validationHelper.getPhoneNumberValidationRule(phone.CountryLookupId, null);
        return (validationRule && validationRule.ValidationErrorMessageLookupId) ? cacheStore.GetLocalizedValueByLookupId(validationRule.ValidationErrorMessageLookupId) : "";
    }

    public static getMask(phone: PhoneNumber): string {
        let validationRule = cacheStore.validationHelper.getPhoneNumberValidationRule(phone.CountryLookupId, null);
        return validationRule ? validationRule.EditMaskPattern : "";
    }

    public static validatePhone(phone: PhoneNumber, isEmptyValid: boolean = false): boolean {
        if (phone.Number == null || phone.Number.length === 0)
            return isEmptyValid;

        // fetch phoneNUmber validation info
        let phonevalidationRule = cacheStore.validationHelper.getPhoneNumberValidationRule(phone.CountryLookupId, null);
        let phonevalidationRegex: string = phonevalidationRule ? phonevalidationRule.ValidationRegexPattern : "";
        let isValid = true;

        if (phonevalidationRegex && phonevalidationRegex.length > 0) {
            let regexp = new RegExp(phonevalidationRegex, "i");
            isValid = ValidationHelper.isExactMatch(phone.Number, regexp);
        }

        return isValid;
    }

    /**
     * Get the primary phone number according to the following priority:
     *  1. preferred personal phone
     *  2. preferred home phone
     *  3. non preferred personal mobile phone
     *  4. non preferred personal phone
     *  5. non preferred work mobile phone
     *  6. non preferred work phone
     */
    public static GetPrimaryPhoneNumber(phones: PhoneNumber[]): PhoneNumber {
        //
        // When changing this method, don't forget to change the equivalent code
        // in Core.Helpers.PhoneHelper.GetPrimaryPhoneNumber in the Myle.Api.Pdf project.
        //
        if (!phones?.any())
            return null;

        const phoneTypeId = cacheStore.getLookupByPhoneType(PhoneType.Phone)?.LookupID;
        const mobileTypeId = cacheStore.getLookupByPhoneType(PhoneType.Mobile)?.LookupID;

        const preferredPhones = phones?.filter(p => p.IsPreferred) ?? [];
        const primaryPreferredPhone = preferredPhones.firstOrDefault(p => p.PhoneTypeLookupId === mobileTypeId)
            ?? preferredPhones.firstOrDefault(p => p.PhoneTypeLookupId === phoneTypeId);

        if (primaryPreferredPhone)
            return primaryPreferredPhone;

        let nonPreferredPhones = phones?.filter(p => !p.IsPreferred) ?? [];

        const personalContactPointTypeId = cacheStore.getLookupByContactPointType(ContactPointType.Personal)?.LookupID;
        const workContactPointTypeId = cacheStore.getLookupByContactPointType(ContactPointType.Work)?.LookupID;

        return nonPreferredPhones.firstOrDefault(p => p.PhoneTypeLookupId === mobileTypeId && p.ContactPointTypeLookupId === personalContactPointTypeId)
            ?? nonPreferredPhones.firstOrDefault(p => p.PhoneTypeLookupId === phoneTypeId && p.ContactPointTypeLookupId === personalContactPointTypeId)
            ?? nonPreferredPhones.firstOrDefault(p => p.PhoneTypeLookupId === mobileTypeId && p.ContactPointTypeLookupId === workContactPointTypeId)
            ?? nonPreferredPhones.firstOrDefault(p => p.PhoneTypeLookupId === phoneTypeId && p.ContactPointTypeLookupId === workContactPointTypeId);
    }
}

export { PhoneNumber };
